import React, { useState, useCallback } from 'react';
import { ArrowLeft, Plus, Edit2, Trash2, Search, Briefcase } from 'lucide-react';
import { Servicio } from '../../types';
import ApiService from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks/useApi';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';
import MultiSelect from '../MultiSelect';

interface ServiciosConfigProps {
  onBack: () => void;
}

interface ServicioFormData {
  tecnologia: string;
  categoria: string;
  unidadPrimariaId: number;
  afinidadSecundaria: string;
  areasSoporteIds: number[];
  casosUso: string;
}

const ServiciosConfig: React.FC<ServiciosConfigProps> = ({ onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingServicio, setEditingServicio] = useState<Servicio | null>(null);
  const [formData, setFormData] = useState<ServicioFormData>({
    tecnologia: '',
    categoria: '',
    unidadPrimariaId: 0,
    afinidadSecundaria: '',
    areasSoporteIds: [],
    casosUso: ''
  });

  // Memoizar funciones de API para evitar recreaciones
  const fetchServicios = useCallback(() => ApiService.getServicios(), []);
  const fetchAreas = useCallback(() => ApiService.getAreas(), []);

  // Hooks para datos y operaciones
  const { data: serviciosRaw, loading, error, refetch } = useApi(fetchServicios);
  const { data: areas } = useApi(fetchAreas);

  /**
   * SOLUCIÓN TEMPORAL: Enriquecer servicios con áreas de soporte
   * 
   * El backend actualmente no devuelve el campo areasSoporte en las respuestas,
   * aunque recibe areasSoporteIds en las peticiones POST/PUT.
   * 
   * Esta función reconstituye las áreas de soporte usando:
   * 1. Los datos del backend si ya los incluye
   * 2. localStorage como almacenamiento temporal de los IDs seleccionados
   * 
   * TODO: Eliminar esta lógica cuando el backend sea actualizado para:
   * - Procesar correctamente areasSoporteIds
   * - Devolver areasSoporte en las respuestas GET
   */
  const servicios = React.useMemo(() => {
    if (!serviciosRaw || !areas) return serviciosRaw;
    
    return serviciosRaw.map(servicio => {
      // Si ya tiene areasSoporte como array, no hacer nada
      if (Array.isArray(servicio.areasSoporte)) {
        return servicio;
      }
      
      // Si no tiene areasSoporte o es string vacío, intentar reconstruir desde localStorage
      const servicioKey = `servicio_areas_${servicio.id}`;
      const storedAreasIds = localStorage.getItem(servicioKey);
      
      if (storedAreasIds) {
        try {
          const areaIds: number[] = JSON.parse(storedAreasIds);
          const areasSoporte = areas.filter(area => areaIds.includes(area.id));
          return { ...servicio, areasSoporte };
        } catch (error) {
          console.error('Error parsing stored areas:', error);
        }
      }
      
      return servicio;
    });
  }, [serviciosRaw, areas]);
  const { execute: createServicio, loading: creating } = useAsyncOperation();
  const { execute: updateServicio, loading: updating } = useAsyncOperation();
  const { execute: deleteServicio } = useAsyncOperation();

  // Filtrar servicios por búsqueda
  const filteredServicios = servicios?.filter(servicio =>
    servicio.tecnologia.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (servicio.categoria && servicio.categoria.toLowerCase().includes(searchTerm.toLowerCase())) ||
    servicio.unidadPrimaria.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Manejar apertura de modal
  const handleOpenModal = (servicio?: Servicio) => {
    if (servicio) {
      setEditingServicio(servicio);
      
      // Manejar areasSoporte - priorizar nueva propiedad del backend
      let areasSoporteIds: number[] = [];
      if (servicio.areasSoporteList?.length) {
        // Usar la nueva propiedad del backend
        areasSoporteIds = servicio.areasSoporteList.map(area => area.id);
      } else if (servicio.areasSoporte && Array.isArray(servicio.areasSoporte)) {
        // Fallback al formato anterior (array)
        areasSoporteIds = servicio.areasSoporte.map(area => area.id);
      }
      // Si es string legacy, no podemos convertir a IDs sin más información
      
      setFormData({
        tecnologia: servicio.tecnologia,
        categoria: servicio.categoria || '',
        unidadPrimariaId: servicio.unidadPrimaria.id,
        afinidadSecundaria: servicio.afinidadSecundaria || '',
        areasSoporteIds: areasSoporteIds,
        casosUso: servicio.casosUso || ''
      });
    } else {
      setEditingServicio(null);
      setFormData({
        tecnologia: '',
        categoria: '',
        unidadPrimariaId: areas?.[0]?.id || 0,
        afinidadSecundaria: '',
        areasSoporteIds: [],
        casosUso: ''
      });
    }
    setIsModalOpen(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingServicio(null);
    setFormData({
      tecnologia: '',
      categoria: '',
      unidadPrimariaId: 0,
      afinidadSecundaria: '',
      areasSoporteIds: [],
      casosUso: ''
    });
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.tecnologia.trim() || !formData.unidadPrimariaId) {
      alert('La tecnología y el área son obligatorios');
      return;
    }

    try {
      const submitData = {
        ...formData,
        activo: true
      };

      if (editingServicio) {
        await updateServicio(() => 
          ApiService.updateServicio(editingServicio.id, submitData)
        );
        
        // Guardar áreas de soporte en localStorage (temporal)
        if (formData.areasSoporteIds.length > 0) {
          localStorage.setItem(`servicio_areas_${editingServicio.id}`, JSON.stringify(formData.areasSoporteIds));
        } else {
          localStorage.removeItem(`servicio_areas_${editingServicio.id}`);
        }
      } else {
        const result = await createServicio(() => ApiService.createServicio(submitData));
        
        // Guardar áreas de soporte en localStorage para el nuevo servicio (temporal)
        if (result && formData.areasSoporteIds.length > 0 && typeof result === 'object' && 'id' in result) {
          // Esperar un poco para asegurar que el servicio fue creado
          setTimeout(() => {
            const newServiceId = (result as Servicio).id;
            localStorage.setItem(`servicio_areas_${newServiceId}`, JSON.stringify(formData.areasSoporteIds));
            refetch(); // Refrescar para mostrar las áreas
          }, 500);
        }
      }
      
      handleCloseModal();
      refetch();
    } catch (error) {
      console.error('Error al guardar servicio:', error);
    }
  };

  // Eliminar servicio
  const handleDelete = async (id: number, tecnologia: string) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el servicio "${tecnologia}"?`)) {
      return;
    }

    try {
      await deleteServicio(() => ApiService.deleteServicio(id));
      
      // Limpiar áreas de soporte del localStorage
      localStorage.removeItem(`servicio_areas_${id}`);
      
      refetch();
    } catch (error) {
      console.error('Error al eliminar servicio:', error);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Volver</span>
          </button>
          <div className="flex items-center space-x-2">
            <Briefcase className="w-6 h-6 text-green-600" />
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">Gestión de Servicios</h1>
          </div>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center space-x-2 btn-primary transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nuevo Servicio</span>
        </button>
      </div>

      {/* Búsqueda */}
      <div className="card p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar servicios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Lista de servicios */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
            <thead className="bg-neutral-50 dark:bg-neutral-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Tecnología
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Categoría
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Área
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Áreas de Soporte
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Casos de Uso
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-900 divide-y divide-neutral-200 dark:divide-neutral-700">
              {filteredServicios.map((servicio) => (
                <tr key={servicio.id} className="hover:bg-neutral-50 dark:bg-neutral-800">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-neutral-900 dark:text-neutral-50">{servicio.tecnologia}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs bg-primary/10 dark:bg-primary/20 text-primary rounded-full">
                      {servicio.categoria || 'Sin categoría'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-neutral-900 dark:text-neutral-50">{servicio.unidadPrimaria.nombre}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {servicio.areasSoporteList?.length ? (
                        // Usar la nueva propiedad del backend si está disponible
                        servicio.areasSoporteList.map((area) => (
                          <span
                            key={area.id}
                            className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full"
                          >
                            {area.nombre}
                          </span>
                        ))
                      ) : servicio.areasSoporte ? (
                        Array.isArray(servicio.areasSoporte) ? (
                          servicio.areasSoporte.length > 0 ? (
                            servicio.areasSoporte.map((area) => (
                              <span
                                key={area.id}
                                className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full"
                              >
                                {area.nombre}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-neutral-500 dark:text-neutral-400">Sin soporte</span>
                          )
                        ) : (
                          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                            {servicio.areasSoporte}
                          </span>
                        )
                      ) : (
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">Sin soporte</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-neutral-600 dark:text-neutral-400 max-w-xs truncate">
                      {servicio.casosUso || 'Sin casos de uso definidos'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleOpenModal(servicio)}
                        className="text-green-600 hover:text-green-900 p-1 rounded"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(servicio.id, servicio.tecnologia)}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredServicios.length === 0 && (
            <div className="text-center py-12">
              <Briefcase className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
              <h3 className="mt-2 text-sm font-medium text-neutral-900 dark:text-neutral-50">No hay servicios</h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                {searchTerm ? 'No se encontraron servicios con ese criterio' : 'Comienza creando un nuevo servicio'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar servicio */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50 mb-4">
              {editingServicio ? 'Editar Servicio' : 'Nuevo Servicio'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Tecnología *
                  </label>
                  <input
                    type="text"
                    value={formData.tecnologia}
                    onChange={(e) => setFormData({ ...formData, tecnologia: e.target.value })}
                    className="input-field"
                    placeholder="Ej: React, .NET, Python, etc."
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Categoría
                  </label>
                  <input
                    type="text"
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    className="input-field"
                    placeholder="Ej: Frontend, Backend, DevOps, etc."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Área Responsable *
                </label>
                <select
                  value={formData.unidadPrimariaId}
                  onChange={(e) => setFormData({ ...formData, unidadPrimariaId: parseInt(e.target.value) })}
                  className="input-field"
                  required
                >
                  <option value={0}>Selecciona un área</option>
                  {areas?.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Afinidad Secundaria
                </label>
                <select
                  value={formData.afinidadSecundaria}
                  onChange={(e) => setFormData({ ...formData, afinidadSecundaria: e.target.value })}
                  className="input-field"
                >
                  <option value="">Selecciona un área (opcional)</option>
                  {areas?.map((area) => (
                    <option key={area.id} value={area.nombre}>
                      {area.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <MultiSelect
                options={areas || []}
                selectedIds={formData.areasSoporteIds}
                onChange={(selectedIds) => setFormData({ ...formData, areasSoporteIds: selectedIds })}
                placeholder="Seleccionar áreas de soporte..."
                label="Áreas de Soporte"
              />
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Casos de Uso
                </label>
                <textarea
                  value={formData.casosUso}
                  onChange={(e) => setFormData({ ...formData, casosUso: e.target.value })}
                  className="input-field"
                  rows={3}
                  placeholder="Describe los principales casos de uso de este servicio..."
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating || updating}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {creating || updating ? 'Guardando...' : editingServicio ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiciosConfig;